<script setup lang="ts">
import { computed } from 'vue'
import type { WorkspaceBranding } from '@/types'
import { useWorkspaceBadge } from '@/composables/useWorkspaceBadge'

type BadgeSize = 'small' | 'large'

const props = withDefaults(defineProps<{
  workspaceId: string | number | null | undefined
  name: string
  branding?: Partial<WorkspaceBranding> | null
  size?: BadgeSize
}>(), {
  branding: null,
  size: 'large',
})

const initial = computed(() => props.name.charAt(0).toUpperCase())
const {
  src,
  surface,
  kind,
  handleImageError,
} = useWorkspaceBadge(
  () => props.branding,
  () => props.workspaceId,
)
</script>

<template>
  <div
    class="workspace-badge"
    :class="[
      `workspace-badge--${size}`,
      {
        'workspace-badge--light': src && surface === 'light',
        'workspace-badge--logo': src && kind === 'logo',
      },
    ]"
  >
    <img
      v-if="src"
      :key="src"
      :src="src"
      :alt="name"
      class="workspace-badge__image"
      @error="handleImageError"
    />
    <span v-else class="workspace-badge__initial">{{ initial }}</span>
  </div>
</template>

<style scoped>
.workspace-badge {
  border-radius: 50%;
  background-color: #005261;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.workspace-badge--large {
  width: 32px;
  height: 32px;
  font-size: 14px;
}

.workspace-badge--small {
  width: 24px;
  height: 24px;
  font-size: 11px;
}

.workspace-badge--light {
  background-color: #FFFFFF;
}

.workspace-badge__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.workspace-badge--large.workspace-badge--logo .workspace-badge__image {
  box-sizing: border-box;
  padding: 3px;
}

.workspace-badge--small.workspace-badge--logo .workspace-badge__image {
  box-sizing: border-box;
  padding: 2px;
}

.workspace-badge__initial {
  color: inherit;
  font-weight: 600;
}
</style>
